import React, { useState } from "react";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { LoadingButton } from "@mui/lab";
import { Link } from "react-router-dom";
import {
  Container,
  Grid,
  IconButton,
  Typography,
  Paper,
  Box,
  SvgIcon,
  Alert,
} from "@mui/material";
import SnackbarComponent from "../components/SnackbarComponent";

const LoginComponent = ({ onLogin }) => {
  const getApiBaseUrl = () => {
    // Always use production backend (onrender)
    return import.meta.env.VITE_ENVPROD || 'https://activitynavigator-backend.onrender.com';
  };
  const apiBaseUrl = getApiBaseUrl();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [response, setResponse] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Invalid email address";
    }

    if (!password) {
      newErrors.password = "Password is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    const requestBody = JSON.stringify({
      email_address: email,
      password: password,
    });

    try {
      const response = await fetch(`${apiBaseUrl}/api/users/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: requestBody,
      });

      const responseData = await response.json();

      if (response.ok) {
        const { data } = responseData;
        const { token, role, userId } = data;

        onLogin({ token, role, userId });

        setResponse(response);
        setSnackbarOpen(true);
      } else {
        setSubmitError(responseData.message || "Authentication failed. Please check your credentials.");
        setResponse(response);
        setSnackbarOpen(true);
      }
    } catch (error) {
      setSubmitError("Network error. Please check your connection and try again.");
      setResponse(response);
      setSnackbarOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (errors.email) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.email;
        return newErrors;
      });
    }
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (errors.password) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.password;
        return newErrors;
      });
    }
  };

  return (
    <Grid
      container
      direction="column"
      justifyContent="center"
      alignItems="center"
    >
      <Grid item>
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            Login
          </Typography>
          
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              label="Email"
              type="email"
              fullWidth
              margin="normal"
              variant="outlined"
              value={email}
              onChange={handleEmailChange}
              error={!!errors.email}
              helperText={errors.email}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': { borderColor: 'primary.main' },
                  '&.Mui-focused fieldset': { borderWidth: 2, borderColor: 'primary.main' },
                },
              }}
            />
            <TextField
              label="Password"
              type="password"
              fullWidth
              margin="normal"
              variant="outlined"
              value={password}
              onChange={handlePasswordChange}
              error={!!errors.password}
              helperText={errors.password}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': { borderColor: 'primary.main' },
                  '&.Mui-focused fieldset': { borderWidth: 2, borderColor: 'primary.main' },
                },
              }}
            />
            <Box textAlign="center">
              <LoadingButton
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                loading={isSubmitting}
                disabled={isSubmitting}
                sx={{ mt: 2.5, minHeight: '44px', transition: 'all 0.2s ease-in-out' }}
              >
                Login
              </LoadingButton>
            </Box>
          </form>
          <SnackbarComponent />
        </Paper>
      </Grid>
      <Typography
        variant="outlined"
        component={Link}
        to="/register"
        color="primary"
        sx={{ mt: 1.25 }}
      >
        Not registered? Click here to sign up
      </Typography>
      <SnackbarComponent
        open={snackbarOpen}
        response={response}
        onClose={() => setSnackbarOpen(false)}
      />
    </Grid>
  );
};

export default LoginComponent;
